import { fetchAuthSession } from 'auth-provider';
import { Environment } from 'utils/EnvironmentHelper';
import { HttpMethod } from 'utils/types';

type GatewayFetchProps = {
    path: string;
    method: HttpMethod;
    customHeaders?: Record<string, string>;
    payload?: unknown;
    ignoreErrors?: boolean;
};

export type CancellablePromise<T> = Promise<T> & Readonly<{ cancel: () => void }>;

async function getAccessToken(): Promise<string> {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken.toString() ?? '';
}

/**
 * "Fetches" a response from the API using the requested information.
 * Uses the current Amplify session to ensure the request is authorized.
 * @param input The information used for the request
 * @param init An optional object containing custom settings to apply to the request like the method, body, or headers.
 */
export async function fetchWithAuth(
    input: RequestInfo,
    init?: RequestInit,
): Promise<CancellablePromise<Response>> {
    const token = await getAccessToken();
    const controller = new AbortController();
    const options: RequestInit = {
        ...init,
        headers: {
            ...init?.headers,
            Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
    };
    const fetchPromise = fetch(input, options);

    // Add cancel method to promise
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (fetchPromise as any).cancel = () => controller.abort();

    return fetchPromise;
}

/**
 * Returns a file from the v3 Api.
 * @param path The path used to retrieve the file which is prefixed with a base url that depends on the environment running.
 * @param fileName The name of the file.
 * @param mimeType The mime type of the file (text/plain, image/jpeg, ect..)
 * @param init An optional object containing custom settings to apply to the request like the method, body, or headers.
 */
export async function fetchFileFromApi(
    path: string,
    fileName: string,
    mimeType: string,
    init?: RequestInit,
): Promise<File> {
    const response = await fetchWithAuth(
        `${Environment.apiBaseUrl}/api/v3.0/${path}`,
        init,
    );

    if (!response.ok) {
        throw new Error(response.statusText);
    }

    const blob = await response.blob();

    return new File([blob], fileName, { type: mimeType });
}

/**
 * "Fetches" a response from the API Gateway using the requested information.
 * Uses the current Amplify session to ensure the request is authorized.
 * @param path The path of the API endpoint to fetch from, added to the base Gateway URL.
 * @param method The HTTP method to use for the request.
 * @param headers An optional parameter containing any custom headers to apply to the request.
 * @param payload An optional object containing the JSON body of the request.
 * @param ignoreErrors An optional parameter to determine if the request should throw an error if the response is not ok.
 */
export async function fetchGatewayWithAuth({
    path,
    method,
    customHeaders,
    payload,
    ignoreErrors,
}: GatewayFetchProps): Promise<Response> {
    const token = await getAccessToken();
    let body;
    const headers = new Headers({
        ...customHeaders,
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    });

    if (payload instanceof FormData) {
        body = payload;
        // `fetch` will automatically set the Content-Type and boundary for a `body` of type `FormData` - @avopelius
        headers.delete('Content-Type');
    } else {
        body = JSON.stringify(payload);
        headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(`${Environment.gatewayApiBaseUrl}/${path}`, {
        method,
        headers,
        body,
    });
    if (!response.ok && !ignoreErrors) {
        throw new Error(response.statusText);
    }
    return response;
}

/**
 * Cancels a fetch query if it contains a `cancel` method.
 *
 * @param fetchPromise The promise to cancel.
 */
export function cancelFetch(
    fetchPromise: Promise<unknown> | CancellablePromise<unknown>,
) {
    if ('cancel' in fetchPromise && typeof fetchPromise.cancel === 'function') {
        fetchPromise.cancel();
    }
}
